import { faArrowUp, faExchange } from '@fortawesome/pro-regular-svg-icons'
import {
  ActionIcon,
  Affix,
  Box,
  Button,
  Flex,
  Menu,
  Text,
  Transition,
} from '@mantine/core'
import { useWindowScroll } from '@mantine/hooks'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { getAllProducts } from '@flecto/api/store'
import { useTranslation } from '@flecto/translations'
import { type CoreProduct, type ParameterID } from '@flecto/types'
import { Icon, useMediaQuery } from '@flecto/ui'

import { Carousel } from '../../components/Carousel/Carousel'
import { ListProducts } from '../../components/ListProducts/ListProducts'
import { Page } from '../../components/Page/Page'
import { useStoreDetails } from '../../hooks/useStoreDetails'
import { useStoreProductAvailabilityList } from '../../hooks/useStoreProductAvailabilityList'
import { CardFilters } from './CardFilters'
import { CardNavigationCategories } from './CardNavigationCategories'
import { ListHeaderControls } from './ListHeaderControls'
import { ListNavigationCategories } from './ListNavigationCategories'
import styles from './styles.module.css'
import { useProductListFilters } from './useProductListFilters'

export type PageProductListProps = {
  title?: string
  subtitle?: string
  htmlTitle?: string
  htmlMetaDescription?: string
  htmlMetaOgTitle?: string
  htmlMetaOgDescription?: string
  withOnlyCategoryId?: ParameterID
  withBackgroundImage?: string
  withCategoriesFilter?: boolean
  withCategoriesNavigation?: boolean
}

export function PageProductList({
  title,
  subtitle,
  htmlTitle,
  htmlMetaDescription,
  htmlMetaOgTitle,
  htmlMetaOgDescription,
  withBackgroundImage,
  withCategoriesFilter,
  withCategoriesNavigation,
}: PageProductListProps) {
  const { t } = useTranslation('store', {
    keyPrefix: 'store-page-product-list',
  })

  const PRODUCTS_PER_PAGE = 20

  const store = useStoreDetails()

  const [scroll, scrollTo] = useWindowScroll()

  const { isDesktop } = useMediaQuery()

  const { filters } = useProductListFilters()

  const [currentPage, setCurrentPage] = useState(1)

  const [isSearching, setIsSearching] = useState(false)
  const [isSearchingMore, setIsSearchingMore] = useState(false)
  const [list, setList] = useState<CoreProduct[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const hasMoreProducts = totalCount > list.length
  const memoizedList = useMemo(() => list, [list])

  const { refetchAvailability } = useStoreProductAvailabilityList({
    products: memoizedList,
  })

  const fetchProducts = useCallback(async () => {
    try {
      setList([])
      setTotalCount(0)
      setIsSearching(true)

      const page = 1

      setCurrentPage(page)

      const data = await getAllProducts(store?.domain ?? '', {
        ...filters,
        limit: PRODUCTS_PER_PAGE * page,
        offset: 0,
      })

      setList(data?.product ?? [])
      setTotalCount(data?.total_count ?? 0)

      await refetchAvailability?.()
    } finally {
      setIsSearching(false)
    }
  }, [JSON.stringify(filters), currentPage, store?.domain])

  const fetchMoreProducts = useCallback(async () => {
    try {
      setIsSearchingMore(true)

      const nextPage = currentPage + 1
      setCurrentPage(nextPage)

      const data = await getAllProducts(store?.domain ?? '', {
        ...filters,
        limit: PRODUCTS_PER_PAGE * nextPage,
        offset: 0,
      })

      setList(data?.product ?? [])
      setTotalCount(data?.total_count ?? 0)
      setCurrentPage(nextPage)

      await refetchAvailability?.()
    } finally {
      setIsSearchingMore(false)
    }
  }, [JSON.stringify(filters), currentPage, store?.domain])

  useEffect(() => {
    refetchAvailability?.()
  }, [list])

  const titleComponent =
    !isDesktop && withCategoriesNavigation ? (
      <Flex align='center' justify='space-between'>
        {title}
        <Menu position='bottom-end'>
          <Menu.Target>
            <ActionIcon variant='default' size='sm'>
              <Icon icon={faExchange} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown p='md'>
            <ListNavigationCategories />
          </Menu.Dropdown>
        </Menu>
      </Flex>
    ) : (
      (title ?? t('title'))
    )

  useEffect(() => {
    fetchProducts()
  }, [JSON.stringify(filters)])

  return (
    <Page
      htmlTitle={htmlTitle}
      htmlMetaDescription={htmlMetaDescription}
      htmlMetaOgTitle={htmlMetaOgTitle}
      htmlMetaOgDescription={htmlMetaOgDescription}
      hasCannonical
    >
      <Page.Section
        title={titleComponent}
        subtitle={
          subtitle !== undefined ? subtitle : t('label_search_for_products')
        }
        withBackgroundImage={withBackgroundImage}
      >
        <Box className={styles.content}>
          {isDesktop && (
            <Box>
              <CardFilters withCategories={withCategoriesFilter} mt={76} />
              {withCategoriesNavigation && <CardNavigationCategories />}
            </Box>
          )}
          <Box className={styles.list}>
            <ListHeaderControls
              totalCount={totalCount}
              isSearching={isSearching || isSearchingMore}
              withCategoriesFilter={withCategoriesFilter}
            />
            {isSearching && <Carousel.Loading items={isDesktop ? 4 : 2} />}
            {list.length > 0 && !isSearching && (
              <ListProducts products={list} />
            )}
            <Flex justify='center' mt='lg'>
              {hasMoreProducts && (
                <Button
                  mt='md'
                  size='xl'
                  px={50}
                  loading={isSearchingMore}
                  onClick={() => fetchMoreProducts()}
                >
                  {t('label_button_load_more')}
                </Button>
              )}
              {!isSearching && !isSearchingMore && !hasMoreProducts && (
                <Text mt='lg' c='dimmed'>
                  {t('label_no_more_results')}
                </Text>
              )}
            </Flex>
            <Affix position={{ bottom: 20, right: 20 }}>
              <Transition transition='slide-up' mounted={scroll.y > 0}>
                {(transitionStyles) => (
                  <Button
                    leftSection={<Icon icon={faArrowUp} />}
                    style={transitionStyles}
                    onClick={() => scrollTo({ y: 0 })}
                  >
                    {t('label_scroll_to_top')}
                  </Button>
                )}
              </Transition>
            </Affix>
          </Box>
        </Box>
      </Page.Section>
    </Page>
  )
}
